import { useState, useEffect } from 'react';
import { Card, Input, Button, Form, message, Modal, Progress, Checkbox, Select, TimePicker, DatePicker, Table, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import { apiFetch, apiUrl } from '../api';
import dayjs from 'dayjs';
import { 
  FiUser, FiMail, FiPhone, FiMapPin, FiBriefcase, 
  FiTag, FiCalendar, FiEdit2, FiGlobe 
} from 'react-icons/fi';

const EditProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [hasEmployeeProfile, setHasEmployeeProfile] = useState(false);
  
  const [profileData, setProfileData] = useState({
    username: '',
    name: '',
    role: '',
    department: '',
    employeeId: '',
    email: '',
    phoneNumber: '',
    address: '',
    customFields: {}
  });

  const formatPhone = (phone) => {
    if (!phone || !phone.startsWith('+') || phone.includes(' ')) return phone;
    const cc1 = ['+1', '+7'];
    const cc2 = ['+20','+27','+30','+31','+32','+33','+34','+36','+39','+40','+41','+43','+44','+45','+46','+47','+48','+49','+51','+52','+53','+54','+55','+56','+57','+58','+60','+61','+62','+63','+64','+65','+66','+81','+82','+84','+86','+90','+91','+92','+93','+94','+95','+98'];
    for (const cc of cc1) {
      if (phone.startsWith(cc)) return phone.replace(cc, cc + ' ');
    }
    for (const cc of cc2) {
      if (phone.startsWith(cc)) return phone.replace(cc, cc + ' ');
    }
    if (phone.length > 4) return phone.substring(0, 4) + ' ' + phone.substring(4);
    return phone;
  };

  const [blocks, setBlocks] = useState([]);
  const [form] = Form.useForm();
  const [blockForm] = Form.useForm();
  const [personalScheduleForm] = Form.useForm();
  const [siteConfigForm] = Form.useForm();

  const fetchProfile = async () => {
    try {
      const response = await apiFetch('/api/auth/profile');
      const payload = await response.json();
      if (response.ok && payload.success) {
        const { user, employee } = payload.data;
        setHasEmployeeProfile(!!employee);
        const data = {
          username: user.username || '',
          name: user.name || '',
          role: user.role || '',
          department: employee?.department || 'MO1',
          employeeId: employee?.employeeId || 'DO-102',
          email: user.email || '',
          phoneNumber: employee?.phoneNumber || '',
          address: employee?.address || '',
          avatar: user.avatar || '',
          workingDays: employee?.workingDays || [1, 2, 3, 4, 5],
          customFields: employee?.customFields instanceof Map ? Object.fromEntries(employee.customFields) : (employee?.customFields || {})
        };
        setProfileData(data);
        form.setFieldsValue(data);
        personalScheduleForm.setFieldsValue({
          workingDays: employee?.workingDays || [1, 2, 3, 4, 5],
          workingHoursStart: employee?.workingHours?.start ? dayjs(employee.workingHours.start, 'HH:mm') : dayjs('09:00', 'HH:mm'),
          workingHoursEnd: employee?.workingHours?.end ? dayjs(employee.workingHours.end, 'HH:mm') : dayjs('18:00', 'HH:mm')
        });
      } else {
        message.error(payload.message || 'Failed to load profile details.');
        if (response.status === 404 && payload.message === 'User not found') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.dispatchEvent(new Event('auth-change'));
          navigate('/login');
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      message.error('Connection error.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlocks = async () => {
    try {
      const res = await apiFetch('/api/availability/blocks');
      const payload = await res.json();
      if (res.ok && payload.success) {
        setBlocks(payload.data);
      }
    } catch (err) {
      console.error('Error fetching blocks:', err);
    }
  };

  const fetchSiteConfig = async () => {
    try {
      const res = await apiFetch('/api/site-config');
      const payload = await res.json();
      if (res.ok && payload.success) {
        siteConfigForm.setFieldsValue({
          meetingDuration: payload.data.meetingDuration !== undefined ? payload.data.meetingDuration : 0
        });
      }
    } catch (err) {
      console.error('Error fetching site config:', err);
    }
  };

  const handleSiteConfigSubmit = async (values) => {
    try {
      const res = await apiFetch('/api/site-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meetingDuration: values.meetingDuration })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        message.success('Global meeting duration updated successfully.');
      } else {
        message.error(data.message || 'Failed to update meeting duration.');
      }
    } catch (err) {
      console.error(err);
      message.error('Connection error.');
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchBlocks();
  }, []);

  useEffect(() => {
    if (profileData.role && profileData.role.toLowerCase() === 'admin') {
      fetchSiteConfig();
    }
  }, [profileData.role]);



  const handlePersonalScheduleSubmit = async (values) => {
    try {
      const payload = {
        workingDays: values.workingDays,
        workingHours: {
          start: values.workingHoursStart.format('HH:mm'),
          end: values.workingHoursEnd.format('HH:mm')
        }
      };

      const res = await apiFetch('/api/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        message.success('My working schedule updated successfully.');
        fetchProfile();
      } else {
        message.error(data.message || 'Failed to update schedule.');
      }
    } catch (err) {
      console.error(err);
      message.error('Connection error.');
    }
  };

  const handleAddBlock = async (values) => {
    try {
      const payload = {
        fromDate: values.dateRange[0].format('DD-MM-YYYY'),
        toDate: values.dateRange[1].format('DD-MM-YYYY'),
        startTime: values.startTime ? values.startTime.format('HH:mm') : '',
        endTime: values.endTime ? values.endTime.format('HH:mm') : '',
        reason: values.reason
      };

      const res = await apiFetch('/api/availability/blocks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        message.success('Time slot blocked successfully.');
        blockForm.resetFields();
        fetchBlocks();
      } else {
        message.error(data.message || 'Failed to block time slot.');
      }
    } catch (err) {
      console.error(err);
      message.error('Connection error.');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      const res = await apiFetch(`/api/availability/blocks/${blockId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (res.ok && data.success) {
        message.success('Blocked slot removed.');
        fetchBlocks();
      } else {
        message.error(data.message || 'Failed to remove blocked slot.');
      }
    } catch (err) {
      console.error(err);
      message.error('Connection error.');
    }
  };

  const handleEditSubmit = async (values) => {
    setSaving(true);
    try {
      const response = await apiFetch('/api/auth/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          phoneNumber: values.phoneNumber,
          address: values.address,
          workingDays: values.workingDays
        })
      });

      const payload = await response.json();
      if (response.ok && payload.success) {
        message.success('Profile updated successfully.');
        setIsEditModalOpen(false);
        fetchProfile();
        
        // Sync local storage user
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        user.email = values.email;
        user.name = values.name;
        localStorage.setItem('user', JSON.stringify(user));
        window.dispatchEvent(new Event('auth-change'));
      } else {
        message.error(payload.message || 'Failed to update profile.');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Connection error.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="wm-page p-6 flex items-center justify-center min-h-[300px]">
        <div className="text-slate-500 font-medium text-base">Loading profile details...</div>
      </div>
    );
  }

  // Calculate a mock completion percentage based on filled details
  const filledFieldsCount = Object.values(profileData).filter(Boolean).length;
  const totalFieldsCount = Object.keys(profileData).length;
  const completionPercentage = Math.round((filledFieldsCount / totalFieldsCount) * 100);

  return (
    <div className="wm-page space-y-6">
      
      {/* 1. Header Box (Light Blue Header) */}
      <div className="bg-[#eaf4fc] dark:bg-slate-900/60 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm border border-sky-100/50 dark:border-white/5">
        <div className="flex flex-col md:flex-row items-center gap-6">
          {/* Avatar Area */}
          <div className="relative group cursor-pointer w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 border-white dark:border-slate-800 bg-white dark:bg-slate-700 shadow-md flex items-center justify-center overflow-hidden" onClick={() => document.getElementById('avatarUpload').click()}>
            <img 
              src={profileData.avatar ? (profileData.avatar.startsWith('data:') || profileData.avatar.startsWith('http') ? profileData.avatar : apiUrl(profileData.avatar)) : `https://ui-avatars.com/api/?name=${profileData.name}&background=ffffff&color=03497A&size=128`} 
              alt="Avatar" 
              className="w-full h-full object-cover" 
            />
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-[10px] sm:text-xs text-white font-semibold text-center leading-none px-1">+ Upload Image</span>
            </div>
            <input 
              type="file" 
              id="avatarUpload" 
              accept="image/*" 
              style={{ display: 'none' }} 
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                
                // Read the file as a data URL
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onloadend = () => {
                  const img = new Image();
                  img.src = reader.result;
                  img.onload = async () => {
                    // Create a canvas to resize the image
                    const canvas = document.createElement('canvas');
                    const MAX_WIDTH = 500;
                    const MAX_HEIGHT = 500;
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                      if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                      }
                    } else {
                      if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height;
                        height = MAX_HEIGHT;
                      }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Get compressed base64 string
                    const base64data = canvas.toDataURL('image/jpeg', 0.8);

                    try {
                      message.loading({ content: 'Uploading...', key: 'avatarUpload' });
                      const res = await apiFetch('/api/auth/profile/avatar', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ avatar: base64data })
                      });
                      
                      let data;
                      try {
                        data = await res.json();
                      } catch(jsonErr) {
                         throw new Error(`Server returned non-JSON. Status: ${res.status}`);
                      }

                      if (res.ok && data.success) {
                        message.success({ content: 'Avatar updated successfully!', key: 'avatarUpload' });
                        setProfileData(prev => ({ ...prev, avatar: data.avatarUrl }));
                        const user = JSON.parse(localStorage.getItem('user') || '{}');
                        user.avatar = data.avatarUrl;
                        localStorage.setItem('user', JSON.stringify(user));
                        window.dispatchEvent(new Event('auth-change'));
                      } else {
                        message.error({ content: data?.message || 'Upload failed', key: 'avatarUpload' });
                      }
                    } catch (err) {
                      console.error(err);
                      message.error({ content: 'Failed to upload photo (File might be too large or connection error)', key: 'avatarUpload' });
                    }
                  };
                };
                e.target.value = '';
              }}
            />
          </div>

          {/* User Details info */}
          <div className="text-center md:text-left space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white leading-tight">{profileData.name}</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
              {profileData.email} <span className="mx-2 text-slate-300">|</span> {formatPhone(profileData.phoneNumber) || 'No phone number'}
            </p>
            
            {/* Meta Tags Row */}
            <div className="flex flex-wrap justify-center md:justify-start gap-4 pt-1 text-xs text-slate-600 dark:text-slate-300">
              <span className="flex items-center gap-1.5"><FiBriefcase className="text-sky-600" /> {profileData.department}</span>
              <span className="flex items-center gap-1.5"><FiTag className="text-sky-600" /> {profileData.employeeId}</span>
              <span className="flex items-center gap-1.5"><FiUser className="text-sky-600 text-sm" /> {profileData.role}</span>
              <span className="flex items-center gap-1.5"><FiCalendar className="text-sky-600" /> {new Date().toLocaleDateString('en-GB')}</span>
            </div>
          </div>
        </div>

        {/* Right side Profile Completion Circle */}
        <div className="flex flex-col items-center gap-1 pr-2">
          <Progress 
            type="circle" 
            percent={completionPercentage} 
            size={70} 
            strokeWidth={8}
            strokeColor="#03497a"
            className="font-bold dark:text-white"
          />
          <span className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold uppercase tracking-wider mt-1">Profile Completion</span>
        </div>
      </div>



      {/* 3. Basic Information Card with Pencil Action */}
      <Card className="wm-card-flat dark:bg-vms-navy dark:border-white/10 relative overflow-hidden">
        {/* Card Header with Edit Icon */}
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/5 pb-4 mb-6">
          <div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
              Basic Information
              <span className="text-xs font-normal text-slate-400 bg-slate-50 dark:bg-slate-800 px-2 py-0.5 rounded-full border border-slate-100 dark:border-slate-700">Active</span>
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">Manage your essential details and residential settings.</p>
          </div>
          
          {/* Blue Pencil button */}
          <button 
            onClick={() => setIsEditModalOpen(true)}
            className="flex items-center justify-center w-9 h-9 rounded-lg bg-sky-50 dark:bg-slate-800 text-[#03497a] dark:text-sky-400 border border-sky-100 dark:border-slate-700 hover:bg-[#03497a] hover:text-white dark:hover:bg-[#03497a] dark:hover:text-white transition-all cursor-pointer shadow-sm group"
            title="Edit Profile"
          >
            <FiEdit2 size={16} className="group-hover:text-white group-hover:stroke-white transition-colors" />
          </button>
        </div>

        {/* Info Grid (Values displayed cleanly as text) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-8">
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Full Name</div>
            <div className="mt-1.5 text-[15px] font-bold text-slate-800 dark:text-slate-100">{profileData.name}</div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Username</div>
            <div className="mt-1.5 text-[15px] font-bold text-slate-800 dark:text-slate-100">{profileData.username}</div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Employee ID</div>
            <div className="mt-1.5 text-[15px] font-bold text-slate-800 dark:text-slate-100">{profileData.employeeId}</div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Personal Email ID</div>
            <div className="mt-1.5 text-[15px] font-bold text-slate-800 dark:text-slate-100">{profileData.email || '-'}</div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Phone Number</div>
            <div className="mt-1.5 text-[15px] font-bold text-slate-800 dark:text-slate-100">{formatPhone(profileData.phoneNumber) || '-'}</div>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Department</div>
            <div className="mt-1.5 text-[15px] font-bold text-slate-800 dark:text-slate-100">{profileData.department}</div>
          </div>
          {hasEmployeeProfile && (
            <div>
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">My Working Days</div>
              <div className="mt-1.5 text-[15px] font-bold text-slate-800 dark:text-slate-100">
                {(profileData.workingDays || [])
                  .map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d])
                  .join(', ') || 'None'}
              </div>
            </div>
          )}
          {hasEmployeeProfile && profileData.customFields && Object.entries(profileData.customFields).map(([key, value]) => {
            const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).replace(/_/g, ' ');
            return (
              <div key={key}>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{formattedKey}</div>
                <div className="mt-1.5 text-[15px] font-bold text-slate-800 dark:text-slate-100">{Array.isArray(value) ? value.join(', ') : (value || '-')}</div>
              </div>
            );
          })}
          <div className="md:col-span-3">
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Residential Address</div>
            <div className="mt-1.5 text-[15px] font-bold text-slate-800 dark:text-slate-100 leading-relaxed whitespace-pre-line">
              {profileData.address || 'No residential address configured.'}
            </div>
          </div>
        </div>
      </Card>



      {/* 7. My Working Schedule (Employees & Admins who have Employee Profile) */}
      {hasEmployeeProfile && profileData.role?.toLowerCase() !== 'receptionist' && (
        <Card className="wm-card-flat dark:bg-vms-navy dark:border-white/10 relative overflow-hidden">
          <div className="border-b border-slate-100 dark:border-white/5 pb-4 mb-6">
            <h2 className="text-lg font-bold text-slate-800 dark:text-white">My Working Schedule</h2>
            <p className="text-xs text-slate-400 mt-0.5">Configure your custom working days and shift timings. If not configured, the default office days and timings will apply.</p>
          </div>
          <Form
            form={personalScheduleForm}
            layout="vertical"
            onFinish={handlePersonalScheduleSubmit}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <Form.Item
              name="workingDays"
              label={<span className="font-semibold text-slate-700 dark:text-slate-200">My Working Days</span>}
              className="md:col-span-3"
            >
              <Checkbox.Group
                options={[
                  { label: 'Mon', value: 1 },
                  { label: 'Tue', value: 2 },
                  { label: 'Wed', value: 3 },
                  { label: 'Thu', value: 4 },
                  { label: 'Fri', value: 5 },
                  { label: 'Sat', value: 6 },
                  { label: 'Sun', value: 0 }
                ]}
              />
            </Form.Item>
            <Form.Item
              name="workingHoursStart"
              label={<span className="font-semibold text-slate-700 dark:text-slate-200">Start Time</span>}
            >
              <TimePicker format="HH:mm" minuteStep={5} className="w-full h-10" />
            </Form.Item>
            <Form.Item
              name="workingHoursEnd"
              label={<span className="font-semibold text-slate-700 dark:text-slate-200">End Time</span>}
            >
              <TimePicker format="HH:mm" minuteStep={5} className="w-full h-10" />
            </Form.Item>
            <div className="md:col-span-3 flex justify-end">
              <Button type="primary" htmlType="submit" className="wm-btn-primary h-10 px-6">
                Save My Schedule
              </Button>
            </div>
          </Form>
        </Card>
      )}

      {/* 8. Global Settings (Admin Only) */}
      {profileData.role && profileData.role.toLowerCase() === 'admin' && (
        <Card className="wm-card-flat dark:bg-vms-navy dark:border-white/10 relative overflow-hidden">
          <div className="border-b border-slate-100 dark:border-white/5 pb-4 mb-6">
            <h2 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <FiGlobe className="text-[#03497a] dark:text-sky-400" />
              Global Configuration
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">Manage system-wide defaults and settings.</p>
          </div>
          <Form
            form={siteConfigForm}
            layout="vertical"
            onFinish={handleSiteConfigSubmit}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <Form.Item
              name="meetingDuration"
              label={<span className="font-semibold text-slate-700 dark:text-slate-200">Default Meeting Duration</span>}
              className="md:col-span-1"
            >
              <Select className="h-10">
                <Select.Option value={0}>0 Minutes</Select.Option>
                <Select.Option value={10}>10 Minutes</Select.Option>
                <Select.Option value={20}>20 Minutes</Select.Option>
                <Select.Option value={30}>30 Minutes</Select.Option>
                <Select.Option value={40}>40 Minutes</Select.Option>
                <Select.Option value={50}>50 Minutes</Select.Option>
                <Select.Option value={60}>60 Minutes</Select.Option>
              </Select>
            </Form.Item>
            <div className="md:col-span-3 flex justify-end">
              <Button type="primary" htmlType="submit" className="wm-btn-primary h-10 px-6">
                Save Global Settings
              </Button>
            </div>
          </Form>
        </Card>
      )}

      {/* 6. Personal Blocked Slots */}
      {profileData.role?.toLowerCase() !== 'receptionist' && (
      <Card className="wm-card-flat dark:bg-vms-navy dark:border-white/10 relative overflow-hidden">
        <div className="border-b border-slate-100 dark:border-white/5 pb-4 mb-6">
          <h2 className="text-lg font-bold text-slate-800 dark:text-white">Block Time Slots</h2>
          <p className="text-xs text-slate-400 mt-0.5">Define slots where you are unavailable for visitor meetings.</p>
        </div>

        {!hasEmployeeProfile ? (
          <div className="text-slate-400 text-sm font-medium py-4">
            Your account does not have an associated Employee profile. Personal time blocking is only available for employees.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Add Block Form */}
            <div className="lg:col-span-1 border-r border-slate-100 dark:border-white/5 pr-0 lg:pr-8">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">Add Unavailable Slot</h3>
              <Form form={blockForm} layout="vertical" onFinish={handleAddBlock}>
                <Form.Item
                  name="dateRange"
                  label="Date Range"
                  rules={[{ required: true, message: 'Please select a date range' }]}
                >
                  <DatePicker.RangePicker format="DD-MM-YYYY" className="w-full h-10" disabledDate={current => current && current.isBefore(dayjs().startOf('day'))} />
                </Form.Item>
                <div className="grid grid-cols-2 gap-4">
                  <Form.Item
                    name="startTime"
                    label="From Time (Optional)"
                  >
                    <TimePicker format="HH:mm" minuteStep={5} className="w-full h-10" />
                  </Form.Item>
                  <Form.Item
                    name="endTime"
                    label="To Time (Optional)"
                  >
                    <TimePicker format="HH:mm" minuteStep={5} className="w-full h-10" />
                  </Form.Item>
                </div>
                <Form.Item
                  name="reason"
                  label="Reason"
                  rules={[{ required: true, message: 'Please enter a reason' }]}
                >
                  <Input placeholder="e.g. Lunch, Doctor Appointment" className="h-10" />
                </Form.Item>
                <Button type="primary" htmlType="submit" className="w-full wm-btn-primary h-10">
                  Block Slot
                </Button>
              </Form>
            </div>

            {/* Blocked Slots List */}
            <div className="lg:col-span-2 overflow-x-auto">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-4">Current Blocked Slots</h3>
              <Table
                dataSource={blocks}
                rowKey="_id"
                pagination={{ pageSize: 5 }}
                columns={[
                  {
                    title: 'Date Range',
                    key: 'dateRange',
                    render: (_, record) => {
                      const from = dayjs(record.fromDate).format('DD-MM-YYYY');
                      const to = dayjs(record.toDate).format('DD-MM-YYYY');
                      return from === to ? from : `${from} to ${to}`;
                    }
                  },
                  {
                    title: 'Time Range',
                    key: 'range',
                    render: (_, record) => (record.startTime && record.endTime) ? `${record.startTime} - ${record.endTime}` : 'Whole Day'
                  },
                  {
                    title: 'Reason',
                    dataIndex: 'reason',
                    key: 'reason'
                  },
                  {
                    title: 'Action',
                    key: 'action',
                    render: (_, record) => (
                      <Popconfirm
                        title="Are you sure you want to unblock this slot?"
                        onConfirm={() => handleDeleteBlock(record._id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button type="link" danger className="p-0">Unblock</Button>
                      </Popconfirm>
                    )
                  }
                ]}
                locale={{ emptyText: 'No blocked slots configured' }}
              />
            </div>
          </div>
        )}
      </Card>
      )}

      {/* 4. Edit Profile Modal Popup Box */}
      <Modal
        title={null}
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        closeIcon={null}
        forceRender
        centered
        width={540}
        styles={{
          content: {
            padding: '24px 32px 32px',
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            border: '1px solid #f0f0f0'
          }
        }}
        style={{ top: 50 }}
      >
        <div className="relative">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/5 pb-4 mb-6">
            <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
              <FiEdit2 className="text-[#03497a] dark:text-sky-400" />
              Edit Profile Info
            </h2>
            <button
              onClick={() => setIsEditModalOpen(false)}
              className="text-slate-400 hover:text-slate-600 transition-colors text-lg cursor-pointer"
            >
              ✕
            </button>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleEditSubmit}
            className="space-y-4"
            requiredMark={false}
          >
            <Form.Item
              name="name"
              label={<span className="font-semibold text-slate-700 dark:text-slate-200">Full Name <span className="text-rose-500">*</span></span>}
              rules={[
                { required: true, message: 'Name is required' }
              ]}
            >
              <Input size="large" prefix={<FiUser className="text-sky-600" />} placeholder="Enter full name" className="border-slate-200 h-11" />
            </Form.Item>

            <Form.Item
              name="email"
              label={<span className="font-semibold text-slate-700 dark:text-slate-200">Email Address <span className="text-rose-500">*</span></span>}
              rules={[
                { required: true, message: 'Email address is required' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input size="large" prefix={<FiMail className="text-sky-600" />} placeholder="Enter email address" className="border-slate-200 h-11" />
            </Form.Item>

            <Form.Item
              name="phoneNumber"
              label={<span className="font-semibold text-slate-700 dark:text-slate-200">Phone Number</span>}
            >
              <Input size="large" prefix={<FiPhone className="text-sky-600" />} placeholder="Enter phone number" className="border-slate-200 h-11" />
            </Form.Item>

            <Form.Item
              name="address"
              label={<span className="font-semibold text-slate-700 dark:text-slate-200">Residential Address</span>}
            >
              <Input.TextArea rows={3} placeholder="Enter residential address" className="border-slate-200" style={{ resize: 'none' }} />
            </Form.Item>

            {hasEmployeeProfile && (
              <Form.Item
                name="workingDays"
                label={<span className="font-semibold text-slate-700 dark:text-slate-200">My Working Days</span>}
              >
                <Checkbox.Group
                  options={[
                    { label: 'Mon', value: 1 },
                    { label: 'Tue', value: 2 },
                    { label: 'Wed', value: 3 },
                    { label: 'Thu', value: 4 },
                    { label: 'Fri', value: 5 },
                    { label: 'Sat', value: 6 },
                    { label: 'Sun', value: 0 }
                  ]}
                />
              </Form.Item>
            )}

            {/* Footer Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-white/5 mt-8">
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                className="px-6 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium text-sm transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2.5 rounded-lg bg-[#03497a] hover:bg-[#007ac1] text-white font-semibold text-sm transition-colors shadow-sm disabled:opacity-50 cursor-pointer"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default EditProfile;
